function readEntries(reader) {
  return new Promise((resolve, reject) => {
    reader.readEntries(resolve, reject);
  });
}

function getFileKey(file) {
  return [
    file.name,
    file.size,
    file.lastModified,
    file.type,
    file.webkitRelativePath || ''
  ].join('::');
}

function pushUniqueFile(files, file, seenKeys) {
  if (!file) {
    return;
  }

  const key = getFileKey(file);
  if (seenKeys.has(key)) {
    return;
  }

  seenKeys.add(key);
  files.push(file);
}

function entryToFile(entry) {
  return new Promise((resolve, reject) => {
    entry.file(resolve, reject);
  });
}

async function readDirectoryEntries(entry) {
  const reader = entry.createReader();
  const entries = [];

  while (true) {
    const batch = await readEntries(reader);

    if (!batch.length) {
      break;
    }

    entries.push(...batch);
  }

  return entries;
}

async function traverseEntry(entry, files, seenKeys) {
  if (entry.isFile) {
    const file = await entryToFile(entry);
    pushUniqueFile(files, file, seenKeys);
    return;
  }

  if (entry.isDirectory) {
    const entries = await readDirectoryEntries(entry);
    await Promise.all(entries.map(childEntry => traverseEntry(childEntry, files, seenKeys)));
  }
}

export async function getDroppedFiles(dataTransfer) {
  if (!dataTransfer) {
    return [];
  }

  const files = [];
  const seenKeys = new Set();
  const items = Array.from(dataTransfer.items || []).filter(item => item.kind === 'file');
  const fallbackFiles = Array.from(dataTransfer.files || []);

  // For plain multi-file drags (no directories), the FileList is the most reliable source.
  // Some browsers expose entries for files, but async entry reads can be incomplete.
  const hasDirectoryEntry = items.some(item => item.webkitGetAsEntry?.()?.isDirectory);

  if (!hasDirectoryEntry) {
    fallbackFiles.forEach(file => pushUniqueFile(files, file, seenKeys));

    // Keep item.getAsFile() as a supplemental source for browsers with partial FileList support.
    items.forEach(item => pushUniqueFile(files, item.getAsFile?.(), seenKeys));
    return files;
  }

  for (const item of items) {
    const entry = item.webkitGetAsEntry?.();

    if (!entry) {
      pushUniqueFile(files, item.getAsFile?.(), seenKeys);
      continue;
    }

    await traverseEntry(entry, files, seenKeys);
  }

  fallbackFiles.forEach(file => pushUniqueFile(files, file, seenKeys));

  return files;
}
